import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { cn } from '../../lib/cn'
import { errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useT, useTNode } from '../../lib/i18n'
import { passwordStrength } from '../../lib/validate'
import type { FieldErrors } from '../../lib/validate'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { InlineError } from '../../components/ui/ErrorState'
import { BootScreen } from '../../shell/ProtectedRoute'
import { AuthLayout } from './AuthLayout'
import { STRENGTH_KEY, emailProblem, newPasswordProblem } from './validation'
import { DEFAULT_DESTINATION, SocialReturnScreen, SocialSignIn, readSocialReturn } from './social'

type SignupField = 'name' | 'email' | 'password' | 'orgName'

const Signup = (): ReactElement => {
  const { signup, status } = useAuth()
  const t = useT()
  const tn = useTNode()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const [name, setName] = useState('')
  const [orgName, setOrgName] = useState('')
  const [email, setEmail] = useState(() => params.get('email') ?? '')
  const [password, setPassword] = useState('')
  /**
   * The referral code from `?ref=`, captured on mount.
   *
   * Component state seeded from the query string, exactly as `email` above is — the convention
   * this card already uses for a value that arrives in the URL and has to survive until submit.
   * Nothing is persisted: the code lives on the link, so a visitor who navigates away and comes
   * back through the same link brings it with them again, and one who arrives by another route
   * was not referred. Storing it would mean a stale code attaching itself to an unrelated signup
   * days later.
   *
   * It is never rendered and never validated here. Whether it names a real workspace is the
   * server's question, and the only answer that changes anything for this form is none at all.
   */
  const [referralCode] = useState(() => params.get('ref')?.trim() ?? '')
  const [errors, setErrors] = useState<FieldErrors<SignupField>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const socialReturn = readSocialReturn(params)
  const destination =
    socialReturn?.kind === 'completing' ? socialReturn.returnTo : DEFAULT_DESTINATION

  /**
   * Sends someone with an existing account to the sign-in card rather than clearing this one:
   * on the sign-up page, "sign in with your password" means a different card, not this form.
   */
  const leaveForSignIn = (): void => {
    const carried = params.get('email')
    const search = carried ? `?email=${encodeURIComponent(carried)}` : ''
    // `replace`, so the browser's back button does not return to the spent callback URL.
    void navigate(`/login${search}`, { replace: true })
  }

  if (status === 'loading') return <BootScreen />
  if (status === 'authenticated') return <Navigate to={destination} replace />

  if (socialReturn) {
    return (
      <SocialReturnScreen
        outcome={socialReturn}
        returnTo={destination}
        onUsePassword={leaveForSignIn}
      />
    )
  }

  const strength = passwordStrength(password)

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    // Each required field carries its own whole sentence rather than `Enter ${noun}` — a noun
    // slotted into a frame is a sentence a translator cannot decline.
    const nameFault = name.trim() === '' ? 'auth.signup.nameRequired' : null
    const orgFault = orgName.trim() === '' ? 'auth.signup.orgRequired' : null
    const emailFault = emailProblem(email)
    const passwordFault = newPasswordProblem(password)

    setErrors({
      name: nameFault === null ? undefined : t(nameFault),
      orgName: orgFault === null ? undefined : t(orgFault),
      email: emailFault === null ? undefined : t(emailFault),
      password: passwordFault === null ? undefined : t(passwordFault),
    })
    setFormError(null)
    if (
      nameFault !== null ||
      orgFault !== null ||
      emailFault !== null ||
      passwordFault !== null
    ) {
      return
    }

    setSubmitting(true)
    try {
      await signup({
        name: name.trim(),
        orgName: orgName.trim(),
        email: email.trim(),
        password,
        /* Omitted rather than sent empty: the field is optional on the wire, and an empty string
           would be a code the server then has to recognise as "no code". */
        ...(referralCode === '' ? {} : { referralCode }),
      })
      await navigate(destination, { replace: true })
    } catch (error) {
      setFormError(errorMessage(error))
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title={t('auth.signup.title')}
      subtitle={t('auth.signup.subtitle')}
      footer={
        <p className="t-small text-ink-mute">
          {tn('auth.signup.haveAccount', {
            signin: (chunk) => (
              <Link
                to="/login"
                className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
              >
                {chunk}
              </Link>
            ),
          })}
        </p>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        className="flex flex-col gap-4"
      >
        {formError ? <InlineError>{formError}</InlineError> : null}

        <Field label={t('auth.signup.nameLabel')} error={errors.name} required>
          <Input
            name="name"
            value={name}
            autoComplete="name"
            autoFocus
            onChange={(event) => {
              setName(event.target.value)
            }}
          />
        </Field>

        <Field
          label={t('auth.signup.orgLabel')}
          hint={t('auth.signup.orgHint')}
          error={errors.orgName}
          required
        >
          <Input
            name="organization"
            value={orgName}
            autoComplete="organization"
            onChange={(event) => {
              setOrgName(event.target.value)
            }}
          />
        </Field>

        <Field label={t('auth.fields.email')} error={errors.email} required>
          <Input
            type="email"
            name="email"
            value={email}
            autoComplete="email"
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
        </Field>

        <Field label={t('auth.fields.password')} error={errors.password} required>
          <Input
            type="password"
            name="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => {
              setPassword(event.target.value)
            }}
          />
        </Field>

        {password ? (
          <div className="flex items-center gap-2" aria-live="polite">
            <span className="flex flex-1 gap-1">
              {[1, 2, 3].map((step) => (
                <span
                  key={step}
                  className={cn(
                    'h-1 flex-1 rounded-full',
                    strength >= step ? 'bg-brass' : 'bg-sunken',
                  )}
                />
              ))}
            </span>
            <span className="t-mono text-ink-mute">{t(STRENGTH_KEY[strength])}</span>
          </div>
        ) : null}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
          {t('auth.signup.submit')}
        </Button>

        <p className="t-small text-ink-mute">{t('auth.signup.terms')}</p>
      </form>

      <SocialSignIn returnTo={destination} className="mt-5" />
    </AuthLayout>
  )
}

export default Signup
